/** @jsxRuntime classic */
/** @jsx jsx */
import { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { css, jsx } from '@emotion/react';

import { BlockNoteView, useCreateBlockNote } from '@blocknote/react';

import MuiDialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Chip from '@mui/material/Chip';

import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';

import { ANALYSIS_BLOCKS } from './blocks/blocks';
import { useUserQuery } from '../../actions/UserActions';
import { useCreditsQuery } from '../../actions/CreditsActions';

import { UpgradeButton } from '../common/Buttons';

import ModelSelector from '../thread/ModelSelector';

export default function AddBlockDialog({ open, onClose }) {
  const { t } = useTranslation();
  const { user } = useUserQuery();
  const { credits } = useCreditsQuery({ enabled: true });
  const [template, setTemplate] = useState(ANALYSIS_BLOCKS[0].type);
  const [group, setGroup] = useState('');
  const editor = useCreateBlockNote();

  const block = ANALYSIS_BLOCKS.find((block) => block.type === template);

  const noCredits = credits?.generation?.credits === 0;

  const isPaying =
    user?.workspace?.type === 'stripe' ||
    user?.workspace?.type === 'enterprise';

  useEffect(() => {
    (async () => {
      const blocks = await editor.tryParseMarkdownToBlocks(
        block?.example || ''
      );
      editor.replaceBlocks(editor.document, blocks);
    })();
  }, [template]);

  const ANALYSIS_GROUPS = useMemo(() => {
    const groups = [];
    ANALYSIS_BLOCKS.forEach((block) => {
      if (!groups.includes(block.group)) {
        groups.push(block.group);
      }
    });
    return groups;
  }, []);

  return (
    <MuiDialog
      open={open}
      onClose={() => onClose()}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: '#e8edf7',
          height: 700,
        },
      }}
    >
      <DialogTitle>{t('Lägg till ett nytt AI-block')}</DialogTitle>
      <DialogContent
        css={css`
          display: flex;
        `}
      >
        <aside>
          {/*
          <Typography>{t('Grupper')}</Typography>
          <List
            sx={{
              width: '100%',
              maxWidth: 360,
              paddingRight: '0.5rem',
              boxSizing: 'border-box',
            }}
          >
            {ANALYSIS_GROUPS.map((_group) => (
              <ListItem
                key={_group}
                button
                onClick={() =>
                  setGroup((group) => (group === _group ? '' : _group))
                }
                css={css`
                  background-color: ${group === _group
                    ? 'var(--primary-8p)'
                    : 'transparent'};
                  color: ${group === _group
                    ? 'var(--primary-main)'
                    : 'var(--text-secondary)'};
                `}
              >
                <Typography>{_group}</Typography>
              </ListItem>
            ))}
          </List>
          */}
          <Typography>{t('AI-block')}</Typography>
          <List
            css={css`
              width: 250px;
              max-height: 534px;
              overflow-y: auto;
              padding-right: 0.5rem;
              box-sizing: border-box;
              margin-top: 0.5rem;
              padding-top: 0;
              &::-webkit-scrollbar-track {
                background: transparent;
              }
            `}
          >
            {ANALYSIS_BLOCKS.filter(
              (block) => !group || block.group === group
            ).map((block) => (
              <ListItem
                key={block.id}
                button
                onClick={() => setTemplate(block.type)}
                css={css`
                  background-color: ${template === block.type
                    ? 'var(--primary-8p)'
                    : 'transparent'};
                  color: ${template === block.type
                    ? 'var(--primary-main)'
                    : 'var(--text-secondary)'};
                `}
              >
                {!isPaying && !block.free ? (
                  <ListItemIcon>
                    <Chip label={t('Pro')} size="small" color="primary" />
                  </ListItemIcon>
                ) : null}
                <ListItemText>{block.title}</ListItemText>
              </ListItem>
            ))}
          </List>
        </aside>
        <div
          css={css`
            padding-bottom: 20px;
            display: flex;
            flex-direction: column;
            background: #fff;
            box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
            width: 100%;
            padding: 1rem;
            gap: 0.2rem;
          `}
        >
          <IconButton
            css={css`
              border: 1px solid #6c6c6c;
              border-radius: 0.5rem;
              position: absolute;
              top: 1rem;
              right: 1rem;
            `}
            onClick={() => onClose()}
            size="small"
          >
            <CloseOutlinedIcon />
          </IconButton>
          <Typography
            variant="span"
            css={css`
              color: var(--Light-Text-Disabled, rgba(0, 0, 0, 0.38));
              font-family: Lato;
              font-size: 15px;
              font-style: normal;
              font-weight: 700;
              line-height: 150%; /* 22.5px */
              letter-spacing: 0.15px;
            `}
          >
            {block?.title}
          </Typography>
          <Typography
            variant="subtitle1"
            css={css`
              display: flex;
              align-items: center;
              gap: 0.5rem;
            `}
          >
            {block?.subtext}
          </Typography>
          <Typography
            variant="subtitle1"
            gutterBottom
            css={css`
              display: flex;
              align-items: center;
              gap: 0.5rem;
            `}
          >
            <TextSnippetIcon css={{ color: '#0000008A' }} />
            {t('Exempel')}
          </Typography>
          <BlockNoteView
            css={css`
              max-height: 534px;
              overflow-y: auto;
              .bn-editor {
                padding-inline: 0;
              }
            `}
            editor={editor}
            editable={false}
            theme="light"
          />
        </div>
      </DialogContent>
      <DialogActions>
        {user?.workspace?.allow_gpt ? <ModelSelector /> : null}
        <Typography variant="subtitle2" color="textSecondary">
          {!block.free && !isPaying
            ? t('Detta AI-block är inte tillgängligt i din prenumeration')
            : noCredits
            ? t('Du har inga AI-svar kvar att använda')
            : t('Alla AI-block förbrukar {{cost}} AI-svar', {
                cost:
                  user?.workspace?.allow_gpt && user?.default_model !== 'klang'
                    ? 2
                    : 1,
              })}
        </Typography>
        <Button onClick={() => onClose()}>{t('Avbryt')}</Button>
        {(!block.free && !isPaying) || noCredits ? (
          <UpgradeButton
            event="add_block_upgrade"
            text={t('Uppgradera')}
            onClick={() => onClose()}
          />
        ) : (
          <Button
            disabled={(!block.free && !isPaying) || noCredits}
            onClick={() => {
              onClose(template);
            }}
            color="primary"
            variant="contained"
          >
            {t('Lägg till')}
          </Button>
        )}
      </DialogActions>
    </MuiDialog>
  );
}
